(() => {
  'use strict';

  const outputSize = 1024;
  const maximumFileSize = 10 * 1024 * 1024;
  const supportedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const discCenters = [[597, 320], [754, 320], [910, 321], [595, 483], [753, 484], [909, 484], [593, 645], [751, 646], [906, 646]];
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const numberLabel = (index) => String(index + 1).padStart(2, '0');
  const cleanText = (value) => value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();

  function setEmphasizedText(element, text) {
    element.replaceChildren(...String(text).split(/(\d+(?:[.,]\d+)*)/).map((part, index) => {
      if (index % 2 === 0) return document.createTextNode(part);
      const number = document.createElement('strong');
      number.textContent = part;
      return number;
    }));
  }

  function makeDesignId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function loadImage(source, anonymous = false) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      let timer;
      const finish = (error) => {
        clearTimeout(timer);
        image.onload = null;
        image.onerror = null;
        if (error) reject(error);
        else resolve(image);
      };
      if (anonymous) image.crossOrigin = 'anonymous';
      image.onload = () => finish(image.naturalWidth && image.naturalHeight ? null : new Error('This image could not be decoded.'));
      image.onerror = () => finish(new Error('This image could not be loaded. Please choose a valid JPG, PNG or WEBP.'));
      timer = setTimeout(() => {
        finish(new Error('The image took too long to load. Please try again.'));
        image.src = '';
      }, 20000);
      image.src = source;
    });
  }

  function jpegBlob(canvas) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob((blob) => {
          if (!blob || blob.type !== 'image/jpeg' || !blob.size || blob.size > maximumFileSize) {
            reject(new Error('Your browser could not create a valid JPEG. Please try another browser.'));
          } else resolve(blob);
        }, 'image/jpeg', 0.94);
      } catch {
        reject(new Error('The preview image cannot be exported safely. Reload the page or try another browser.'));
      }
    });
  }

  function initialize(root) {
    if (root.dataset.nfcCustomInitialized) return;
    root.dataset.nfcCustomInitialized = 'true';
    const query = (selector) => root.querySelector(selector);
    const form = query('.nfc-custom-form');
    const controls = query('[data-nfc-editor-controls]');
    const preview = query('[data-nfc-preview]');
    const previewStatus = query('[data-nfc-preview-status]');
    const feedback = query('[data-nfc-feedback]');
    const submit = query('[data-nfc-submit]');
    const music = query('[data-nfc-music]');
    const acknowledgement = query('[data-nfc-acknowledgement]');
    const dialog = query('[data-nfc-crop-dialog]');
    const cropCanvas = query('[data-nfc-crop-canvas]');
    const upload = query('[data-nfc-upload]');
    const replace = query('[data-nfc-replace]');
    const tiles = Array.from(root.querySelectorAll('[data-nfc-tile]'));
    const cassetteThumbs = Array.from(root.querySelectorAll('[data-nfc-cassette-thumb]'));
    const layoutButtons = Array.from(root.querySelectorAll('[data-nfc-layout]'));
    const trackRows = Array.from(root.querySelectorAll('[data-nfc-track]'));
    const slots = [];
    const objectUrls = new Set();
    let config;
    let layoutId = null;
    let imageCount = 0;
    let previewContext;
    let cropContext;
    let baseImage;
    let cassettePreview;
    let ready = false;
    let disposed = false;
    let editingBusy = false;
    let preparing = false;
    let pending = false;
    let externalLocked = false;
    let uncertain = false;
    let submittedDesignId = null;
    let successfulDesignId = null;
    let designId;
    let revision = 0;
    let activeIndex = 0;
    let replacementIndex = null;
    let draft = null;
    let drag = null;
    let returnFocus = null;
    let persistTimer;
    let storedSignature = '';
    let queuedSignature = '';
    let storageProblem = false;
    let saveQueue = Promise.resolve();
    const storageStatus = query('[data-nfc-storage-status]');
    const selectedLayout = () => config?.family === 'cassette' ? config.layouts?.find(layout => layout.id === layoutId) : null;
    const variantPair = () => selectedLayout() || config;
    const wantsMusic = () => !!variantPair()?.musicVariant && !!music?.checked;
    const selectedVariant = () => wantsMusic() ? variantPair().musicVariant : variantPair()?.photoVariant;
    const printLayout = () => ({ id: layoutId, label: selectedLayout().label });
    const printTransform = (value = {}) => ({ x: clamp(Number.isFinite(value.x) ? value.x : 0.5, 0, 1), y: clamp(Number.isFinite(value.y) ? value.y : 0.5, 0, 1), zoom: clamp(Number.isFinite(value.zoom) ? value.zoom : 1, 1, 8) });
    const persistenceOwner = () => `nfc:${config.family}:${selectedVariant().id}`;
    const persistenceSignature = () => [persistenceOwner(), designId, revision, acknowledgement.checked, wantsMusic(), successfulDesignId, pending, uncertain].join('|');
    function persistenceRecord() {
      return { id: designId, schema: config.family === 'cassette' ? 4 : 3, family: config.family, ...(config.family === 'cassette' ? { printLayout: printLayout() } : {}), imageCount, variantId: String(selectedVariant().id), revision, activeIndex, music: wantsMusic(), acknowledged: acknowledgement.checked, successfulDesignId, pending, uncertain,
        tracks: trackRows.map(row => ({ title: row.querySelector('[data-nfc-track-title]').value, artist: row.querySelector('[data-nfc-track-artist]').value, version: row.querySelector('[data-nfc-track-version]').value })),
        slots: slots.map(slot => slot ? { file: slot.file, blob: slot.blob, crop: slot.crop, width: slot.width, height: slot.height, confirmed: slot.confirmed, ...(config.family === 'cassette' ? { printTransform: printTransform(slot.printTransform), printTransforms: slot.printTransforms || {} } : {}) } : null) };
    }
    function persistNow() {
      clearTimeout(persistTimer);
      if (!ready || disposed || !config || !slots.some(Boolean)) return saveQueue;
      const signature = persistenceSignature();
      if (storedSignature === signature) return saveQueue;
      const record = persistenceRecord();
      const owner = persistenceOwner();
      queuedSignature = signature;
      root.dataset.designStorage = 'saving';
      saveQueue = saveQueue.catch(() => {}).then(async () => {
        if (!window.NFCDesignStore) throw new Error('Private storage is unavailable.');
        await window.NFCDesignStore.save(owner, record);
        storedSignature = signature;
        storageProblem = false;
        root.dataset.designStorage = 'saved';
        if (designId === record.id && persistenceOwner() === owner && !disposed) {
          const url = new URL(window.location.href);
          url.searchParams.set('design', record.id);
          url.searchParams.set('variant', record.variantId);
          window.history.replaceState({}, '', url);
        }
        if (storageStatus) storageStatus.textContent = 'Saved here for 7 days, even after adding to cart.';
      }).catch(() => {
        storageProblem = true;
        root.dataset.designStorage = 'unavailable';
        if (storageStatus) storageStatus.textContent = 'Design not saved. Keep this page open; you may need to upload again.';
      });
      return saveQueue;
    }
    function schedulePersistence() {
      if (!ready || disposed || editingBusy || !slots.some(Boolean)) return;
      const signature = persistenceSignature();
      if (signature === storedSignature || signature === queuedSignature) return;
      clearTimeout(persistTimer);
      persistTimer = setTimeout(() => { void persistNow(); }, 160);
    }
    async function restoreDesign() {
      if (!window.NFCDesignStore) return;
      const requested = new URL(window.location.href).searchParams.get('design');
      try {
        let record = await window.NFCDesignStore.load(persistenceOwner(), requested);
        let legacy = false;
        if (!record && config.family === 'record') {
          record = await window.NFCDesignStore.load(`${config.photoVariant.id}:${config.musicVariant.id}`, requested);
          legacy = !!record;
        }
        if (!record) {
          if (requested) announce('No saved design in this browser. Your cart keeps its uploaded preview.', 'info');
          return;
        }
        const oldCassette = config.family === 'cassette' && record.schema === 3 && !record.printLayout && layoutId === 'side-panel';
        const matchingSchema = config.family === 'cassette' ? oldCassette || record.schema === 4 && record.printLayout?.id === layoutId && record.printLayout?.label === selectedLayout().label : record.schema === 3;
        const matchingScope = legacy ? record.schema === 2 && config.family === 'record' : matchingSchema && record.family === config.family && record.imageCount === imageCount && record.variantId === String(selectedVariant().id);
        if (!matchingScope || record.music !== wantsMusic() || !/^[A-Za-z0-9-]{16,80}$/.test(record.id) || !Array.isArray(record.slots) || record.slots.length !== imageCount) {
          announce('Saved design does not match this player / option. Price unchanged. Start fresh or reopen its matching option.', 'info');
          return;
        }
        const restored = [];
        try {
          for (const saved of record.slots) {
            if (!saved) { restored.push(null); continue; }
            if (!(saved.file instanceof Blob) || saved.file.size > maximumFileSize) throw new Error('Saved photo unavailable');
            const slot = { ...saved, sourceUrl: createUrl(saved.file), image: null, croppedImage: null, cropUrl: null };
            restored.push(slot);
            slot.image = await loadImage(slot.sourceUrl);
            slot.width = slot.image.naturalWidth;
            slot.height = slot.image.naturalHeight;
            if (config.family === 'cassette') {
              slot.printTransform = printTransform(saved.printTransform);
              slot.printTransforms = { ...(saved.printTransforms || {}), [layoutId]: slot.printTransform };
              if (oldCassette || slot.width !== slot.height) slot.confirmed = false;
            }
            if (slot.blob instanceof Blob && slot.crop && slot.confirmed) {
              slot.crop = boundedCrop(slot.crop, slot);
              slot.cropUrl = createUrl(slot.blob);
              slot.croppedImage = await loadImage(slot.cropUrl);
            } else slot.confirmed = false;
          }
        } catch (error) { restored.forEach(releaseSlot); throw error; }
        restored.forEach((slot, index) => { slots[index] = slot; });
        designId = record.id;
        revision = Number(record.revision) || 0;
        activeIndex = clamp(Number(record.activeIndex) || 0, 0, imageCount - 1);
        trackRows.forEach((row, index) => {
          const track = record.tracks?.[index] || {};
          row.querySelector('[data-nfc-track-title]').value = String(track.title || '').slice(0, 120);
          row.querySelector('[data-nfc-track-artist]').value = String(track.artist || '').slice(0, 120);
          row.querySelector('[data-nfc-track-version]').value = String(track.version || '').slice(0, 50);
        });
        successfulDesignId = record.successfulDesignId === record.id ? record.id : null;
        acknowledgement.checked = !!record.acknowledged && !oldCassette && (config.family !== 'cassette' || restored.every(slot => slot?.confirmed));
        uncertain = !!record.uncertain || !!record.pending;
        storedSignature = legacy || oldCassette ? '' : persistenceSignature();
        queuedSignature = storedSignature;
        root.dataset.designStorage = 'saved';
        query('[data-nfc-cart-link]').hidden = !successfulDesignId && !uncertain;
        announce(uncertain ? 'Design restored. Check your cart; do not resubmit.' : oldCassette ? 'Side Panel design restored. Reconfirm all 9 full-bleed print compositions; replace non-square originals first.' : successfulDesignId ? 'Added design restored. Edit to create another set.' : 'Design restored.', uncertain ? 'uncertain' : 'info');
      } catch {
        storageProblem = true;
        if (storageStatus) storageStatus.textContent = 'Saved design could not open. Keep this tab open while editing.';
      }
    }
    const locked = () => !ready || disposed || editingBusy || preparing || pending || externalLocked || uncertain;
    const confirmedCount = () => slots.filter((slot) => slot?.confirmed && slot.blob && slot.crop).length;
    const createUrl = (blob) => {
      const url = URL.createObjectURL(blob);
      objectUrls.add(url);
      return url;
    };
    const revokeUrl = (url) => {
      if (!url || !objectUrls.has(url)) return;
      URL.revokeObjectURL(url);
      objectUrls.delete(url);
    };
    const releaseSlot = (slot) => {
      if (!slot) return;
      revokeUrl(slot.sourceUrl);
      revokeUrl(slot.cropUrl);
    };
    const announce = (message, kind = 'error', focus = false) => {
      feedback.textContent = message;
      feedback.dataset.kind = kind;
      if (focus) feedback.focus();
    };

    function changeDesign() {
      revision += 1;
      designId = makeDesignId();
      acknowledgement.checked = false;
      if (!uncertain && !pending) announce('', 'info');
    }

    function trackValues(markInvalid = false) {
      let firstInvalid = null;
      const values = trackRows.map((row) => {
        const titleInput = row.querySelector('[data-nfc-track-title]');
        const artistInput = row.querySelector('[data-nfc-track-artist]');
        const versionInput = row.querySelector('[data-nfc-track-version]');
        const title = cleanText(titleInput.value);
        const artist = cleanText(artistInput.value);
        const version = cleanText(versionInput.value);
        const value = `${title} — ${artist}${version ? ` (${version})` : ''}`;
        const entries = [[titleInput, !!title && title.length <= 120], [artistInput, !!artist && artist.length <= 120], [versionInput, version.length <= 50 && value.length <= 300]];
        entries.forEach(([input, valid]) => {
          if (!valid && !firstInvalid) firstInvalid = input;
          if (markInvalid) input.setAttribute('aria-invalid', String(!valid));
        });
        return value;
      });
      return { values, firstInvalid };
    }

    function synchronize() {
      const isLocked = locked();
      controls.disabled = isLocked;
      form.setAttribute('aria-busy', String(preparing || pending || editingBusy || externalLocked));
      root.dataset.designId = designId || '';
      root.dataset.designRevision = String(revision);
      root.dataset.confirmedCount = String(confirmedCount());
      if (config?.family === 'cassette') root.dataset.layoutId = layoutId || '';
      root.dataset.customState = uncertain ? 'uncertain' : pending ? 'pending' : preparing ? 'preparing' : !ready ? 'unavailable' : successfulDesignId === designId ? 'success' : 'editing';
      const withMusic = wantsMusic();
      if (music) {
        music.disabled = isLocked || (!variantPair()?.musicVariant?.available && !withMusic);
        query('[data-nfc-music-unavailable]').hidden = !!variantPair()?.musicVariant?.available;
        query('[data-nfc-tracks]').hidden = !withMusic;
        query('[data-nfc-music-price]').hidden = !withMusic;
      }
      layoutButtons.forEach(button => {
        button.disabled = isLocked;
        button.setAttribute('aria-pressed', String(button.dataset.nfcLayout === layoutId));
      });
      if (selectedLayout()) {
        setEmphasizedText(query('[data-nfc-layout-status]'), `${selectedLayout().label} · ${selectedVariant().formattedPrice} · 9 cards`);
      }
      trackRows.forEach((row) => row.querySelectorAll('input').forEach((input) => {
        input.disabled = isLocked || !withMusic;
        input.required = withMusic && !input.hasAttribute('data-nfc-track-version');
      }));
      tiles.forEach((tile, index) => {
        const slot = slots[index];
        const edit = tile.querySelector('[data-nfc-edit]');
        tile.dataset.filled = String(!!slot);
        tile.dataset.confirmed = String(!!slot?.confirmed);
        edit.disabled = isLocked;
        edit.setAttribute('aria-label', `${slot ? 'Edit crop for' : 'Add'} photo ${numberLabel(index)}${slot?.confirmed ? ', confirmed' : ''}`);
        tile.querySelector('[data-nfc-replace-button]').disabled = isLocked || !slot;
        tile.querySelector('[data-nfc-remove]').disabled = isLocked || !slot;
        const image = tile.querySelector('[data-nfc-tile-image]');
        image.hidden = !slot;
        if (slot) {
          const source = slot.cropUrl || slot.sourceUrl;
          if (image.getAttribute('src') !== source) image.src = source;
        } else image.removeAttribute('src');
        tile.querySelector('.nfc-custom-tile-empty').hidden = !!slot;
        tile.querySelector('[data-nfc-tile-state]').textContent = slot?.confirmed ? '✓ Crop confirmed' : slot ? 'Confirm crop' : 'Add photo';
      });
      cassetteThumbs.forEach((button, index) => {
        const slot = slots[index];
        const image = button.querySelector('img');
        button.disabled = isLocked;
        button.setAttribute('aria-label', `${slot ? 'Edit crop for' : 'Add'} photo ${numberLabel(index)}${slot?.confirmed ? ', confirmed' : ''}`);
        button.setAttribute('aria-pressed', String(index === activeIndex));
        image.hidden = !slot?.confirmed;
        if (slot?.confirmed) {
          if (image.getAttribute('src') !== slot.cropUrl) image.src = slot.cropUrl;
        } else image.removeAttribute('src');
      });
      const count = confirmedCount();
      const cropsLabel = imageCount === 1 ? 'portrait crop' : `${imageCount} crops`;
      setEmphasizedText(query('[data-nfc-progress]'), `${count} / ${imageCount} ${imageCount === 1 ? 'crop' : 'crops'} confirmed`);
      const variant = config && selectedVariant();
      if (variant) query('[data-nfc-total]').textContent = variant.formattedPrice;
      setEmphasizedText(query('[data-nfc-fee-note]'), query(withMusic ? '[data-nfc-music-fee]' : '[data-nfc-photo-fee]').textContent);
      setEmphasizedText(query('[data-nfc-acknowledgement-text]'), config?.family === 'record' ? `I confirm my ${cropsLabel}${withMusic ? ' and tracks' : ''}. Print as shown.` : config?.family === 'cassette' ? `I confirm my 9 crops and ${selectedLayout()?.label || 'selected layout'} print compositions${withMusic ? ' and tracks' : ''}. Preview, not a factory dieline.` : config?.family === 'discs' ? 'I confirm my 9 crops for discs 10–18. Circular preview is illustrative; no player included.' : `I confirm my ${imageCount === 1 ? '1-person portrait crop' : `${imageCount} square crops`}${withMusic ? ' and tracks' : ''}. Flat preview, not final placement or cut outline.`);
      const complete = count === imageCount && acknowledgement.checked && (!withMusic || !trackValues().firstInvalid);
      submit.disabled = isLocked || !variant?.available || !complete || successfulDesignId === designId;
      setEmphasizedText(submit, preparing ? 'Preparing images…' : pending ? 'Adding your set…' : uncertain ? 'Check your cart first' : successfulDesignId === designId ? 'Design added to cart' : variant?.available ? `Add to cart · ${variant.formattedPrice}` : 'Custom set unavailable');
      query('[data-nfc-readiness]').textContent = successfulDesignId === designId ? 'Already added. Edit to create another set.' : !variant?.available ? 'Option unavailable.' : complete ? 'Ready to add.' : count !== imageCount ? `Confirm ${imageCount === 1 ? 'your portrait' : `${imageCount} crops`}, then approve your design.` : withMusic && trackValues().firstInvalid ? 'Add title + artist for all 9 tracks.' : 'Approve your design to continue.';
      dialog.querySelectorAll('button, input').forEach((control) => { control.disabled = isLocked; });
      query('[data-nfc-upload-button]').disabled = isLocked || slots.every(Boolean);
      query('[data-nfc-upload-button]').textContent = slots.every(Boolean) ? imageCount === 1 ? 'Portrait added' : `All ${imageCount} photos added` : `＋ Choose ${imageCount === 1 ? 'portrait' : 'photos'}${slots.some(Boolean) ? ` (${slots.filter((slot) => !slot).length} left)` : ''}`;
      schedulePersistence();
    }

    function paintFlatArtwork(context, includeGuides) {
      const portrait = config.family === 'rotating';
      context.fillStyle = '#302c28';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = '600 30px sans-serif';
      context.fillText(`${portrait ? 'Portrait' : 'Cassette'} · Flat artwork preview`, outputSize / 2, 46);
      context.font = '20px sans-serif';
      context.fillText('Not a finished-product mockup or final print outline', outputSize / 2, 82);
      slots.forEach((slot, index) => {
        const edge = portrait ? 640 : 244;
        const left = portrait ? 192 : 78 + index % 3 * 312;
        const top = portrait ? 164 : 120 + Math.floor(index / 3) * 280;
        context.fillStyle = '#f1ebe2';
        context.fillRect(left, top, edge, edge);
        const image = slot?.croppedImage || slot?.image;
        if (image) {
          const crop = slot.croppedImage ? { x: 0, y: 0, size: image.naturalWidth } : slot.crop || fitCrop(slot);
          context.drawImage(image, crop.x, crop.y, crop.size, crop.size, left, top, edge, edge);
        }
        if (includeGuides && slot && index === activeIndex) {
          context.strokeStyle = '#a94532';
          context.lineWidth = 3;
          context.strokeRect(left - 4, top - 4, edge + 8, edge + 8);
        }
        context.fillStyle = '#302c28';
        context.font = '600 20px sans-serif';
        context.fillText(portrait ? '01 · Single character portrait' : numberLabel(index), left + edge / 2, top + edge + 18);
      });
      context.font = '19px sans-serif';
      context.fillText(portrait ? 'Character placement and final outline are not confirmed.' : '01–09 · Square artwork crops; not full-card or label dimensions.', outputSize / 2, portrait ? 890 : 984);
      if (portrait) context.fillText('No custom music or custom card artwork included.', outputSize / 2, 922);
    }

    function paintPreview(context, includeGuides) {
      if (config.family === 'cassette') {
        if (!cassettePreview) throw new Error('The cassette placement preview is unavailable. Please reload before customizing.');
        cassettePreview.paint(context, { slots, includeGuides, activeIndex, outputSize, layoutId });
        return;
      }
      context.clearRect(0, 0, outputSize, outputSize);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, outputSize, outputSize);
      if (config.family === 'discs') {
        context.fillStyle = '#352820';
        context.textAlign = 'center';
        context.font = '600 28px sans-serif';
        context.fillText('9 photo discs · NFC stickers 10–18', outputSize / 2, 48);
        slots.forEach((slot, index) => {
          const centerX = 186 + index % 3 * 326;
          const centerY = 224 + Math.floor(index / 3) * 300;
          context.fillStyle = '#242120';
          context.beginPath();
          context.arc(centerX, centerY, 120, 0, Math.PI * 2);
          context.fill();
          const image = slot?.croppedImage || slot?.image;
          if (image) {
            const crop = slot.croppedImage ? { x: 0, y: 0, size: image.naturalWidth } : slot.crop || fitCrop(slot);
            context.save();
            context.beginPath();
            context.arc(centerX, centerY, 86, 0, Math.PI * 2);
            context.clip();
            context.drawImage(image, crop.x, crop.y, crop.size, crop.size, centerX - 86, centerY - 86, 172, 172);
            context.restore();
          }
          if (includeGuides && index === activeIndex) {
            context.strokeStyle = '#a94532';
            context.lineWidth = 3;
            context.beginPath();
            context.arc(centerX, centerY, 124, 0, Math.PI * 2);
            context.stroke();
          }
          context.fillStyle = '#352820';
          context.font = '600 21px sans-serif';
          context.fillText(String(index + 10), centerX, centerY + 145);
        });
        context.font = '19px sans-serif';
        context.fillText('Disc artwork preview · Player not included', outputSize / 2, 995);
        return;
      }
      if (config.family === 'rotating') {
        paintFlatArtwork(context, includeGuides);
        return;
      }
      if (!baseImage) return;
      const scale = Math.min(outputSize / baseImage.naturalWidth, outputSize / baseImage.naturalHeight);
      const width = baseImage.naturalWidth * scale;
      const height = baseImage.naturalHeight * scale;
      const left = (outputSize - width) / 2;
      const top = (outputSize - height) / 2;
      context.drawImage(baseImage, left, top, width, height);
      const drawSlot = (slot, x, y, targetWidth, targetHeight) => {
        const image = slot?.croppedImage || slot?.image;
        if (!image) return;
        const square = Math.min(image.naturalWidth, image.naturalHeight);
        const ratio = targetWidth / targetHeight;
        const sourceWidth = ratio > 1 ? square : square * ratio;
        const sourceHeight = ratio > 1 ? square / ratio : square;
        context.drawImage(image, (image.naturalWidth - sourceWidth) / 2, (image.naturalHeight - sourceHeight) / 2, sourceWidth, sourceHeight, x, y, targetWidth, targetHeight);
      };
      const panel = { x: 124, y: 338, width: 294, height: 278 };
      context.save();
      context.beginPath();
      context.rect(left + panel.x * scale, top + panel.y * scale, panel.width * scale, panel.height * scale);
      context.clip();
      slots.forEach((slot, index) => {
        const cellWidth = panel.width / 3;
        const cellHeight = panel.height / 3;
        const x = left + (panel.x + (index % 3) * cellWidth) * scale;
        const y = top + (panel.y + Math.floor(index / 3) * cellHeight) * scale;
        if (slot) drawSlot(slot, x, y, cellWidth * scale, cellHeight * scale);
        else if (includeGuides) {
          context.fillStyle = '#f5ead924';
          context.fillRect(x, y, cellWidth * scale, cellHeight * scale);
          context.fillStyle = '#fff9ed';
          context.font = `${13 * scale}px sans-serif`;
          context.textAlign = 'center';
          context.textBaseline = 'middle';
          context.fillText(numberLabel(index), x + cellWidth * scale / 2, y + cellHeight * scale / 2);
        }
      });
      context.restore();
      context.save();
      context.beginPath();
      context.arc(left + 278 * scale, top + 479 * scale, 105 * scale, 0, Math.PI * 2);
      context.clip();
      context.drawImage(baseImage, left, top, width, height);
      context.restore();
      if (slots[0]) {
        context.save();
        context.beginPath();
        context.arc(left + 278 * scale, top + 479 * scale, 46 * scale, 0, Math.PI * 2);
        context.clip();
        drawSlot(slots[0], left + 232 * scale, top + 433 * scale, 92 * scale, 92 * scale);
        context.restore();
      }
      slots.forEach((slot, index) => {
        const centerX = left + discCenters[index][0] * width / outputSize;
        const centerY = top + discCenters[index][1] * height / outputSize;
        const radius = 52 * Math.min(width, height) / outputSize;
        context.save();
        context.beginPath();
        context.arc(centerX, centerY, radius, 0, Math.PI * 2);
        if (slot?.image || slot?.croppedImage) {
          context.clip();
          drawSlot(slot, centerX - radius, centerY - radius, radius * 2, radius * 2);
        } else if (includeGuides) {
          context.strokeStyle = '#fff9ed99';
          context.lineWidth = 2;
          context.setLineDash([5, 6]);
          context.stroke();
          context.fillStyle = '#fff9ed';
          context.font = '500 19px sans-serif';
          context.textAlign = 'center';
          context.textBaseline = 'middle';
          context.fillText(numberLabel(index), centerX, centerY);
        }
        context.restore();
        if (includeGuides && slot && index === activeIndex) {
          context.beginPath();
          context.arc(centerX, centerY, radius + 4, 0, Math.PI * 2);
          context.strokeStyle = '#a94532';
          context.lineWidth = 2;
          context.stroke();
        }
      });
    }

    function updatePreview() {
      try {
        if (previewContext && config && (baseImage || cassettePreview || ['rotating', 'discs'].includes(config.family))) paintPreview(previewContext, true);
        if (cassettePreview && selectedLayout()) {
          const guide = query('[data-nfc-print-guide]');
          cassettePreview.paintCard(guide.getContext('2d'), { layoutId, slot: slots[activeIndex], index: activeIndex, width: guide.width, height: guide.height });
          query('[data-nfc-print-guide-label]').textContent = `Photo ${numberLabel(activeIndex)} · ${selectedLayout().label} · ${slots[activeIndex]?.confirmed ? 'placement confirmed' : 'confirm placement'}`;
        }
      } catch (error) {
        if (config?.family !== 'cassette') throw error;
        ready = false;
        previewContext.clearRect(0, 0, outputSize, outputSize);
        previewStatus.textContent = 'Preview unavailable. Custom ordering is disabled.';
        announce(error.message, 'error');
        synchronize();
        return;
      }
      if (ready) previewStatus.textContent = `${slots.filter(Boolean).length} / ${imageCount} ${imageCount === 1 ? 'portrait' : 'photos'} · ${config.family === 'record' ? 'panel & discs · 01–09' : config.family === 'cassette' ? 'cassette cards · 01–09' : config.family === 'discs' ? 'photo discs · 10–18' : 'flat portrait preview'}`;
    }

    function fitCrop(slot) {
      const size = Math.min(slot.width, slot.height);
      return { x: (slot.width - size) / 2, y: (slot.height - size) / 2, size };
    }

    function boundedCrop(crop, slot) {
      const minimumSize = Math.min(slot.width, slot.height) / 8;
      const size = clamp(Number.isFinite(crop.size) ? crop.size : minimumSize, minimumSize, Math.min(slot.width, slot.height));
      return {
        x: clamp(Number.isFinite(crop.x) ? crop.x : 0, 0, slot.width - size),
        y: clamp(Number.isFinite(crop.y) ? crop.y : 0, 0, slot.height - size),
        size
      };
    }

    function drawCrop() {
      if (!draft) return;
      const slot = slots[draft.index];
      draft.crop = boundedCrop(draft.crop, slot);
      const { x, y, size } = draft.crop;
      const edge = cropCanvas.width;
      cropContext.fillStyle = '#ffffff';
      cropContext.fillRect(0, 0, edge, edge);
      cropContext.drawImage(slot.image, x, y, size, size, 0, 0, edge, edge);
      if (config.family === 'cassette') {
        const printCanvas = query('[data-nfc-print-canvas]');
        cassettePreview.paintCard(printCanvas.getContext('2d'), { layoutId, image: cropCanvas, printTransform: draft.printTransform, index: draft.index, width: printCanvas.width, height: printCanvas.height });
        query('[data-nfc-print-layout-label]').textContent = selectedLayout().label;
        query('[data-nfc-print-zoom]').value = String(draft.printTransform.zoom);
        query('[data-nfc-print-zoom-label]').value = `${draft.printTransform.zoom.toFixed(2)}×`;
        query('[data-nfc-print-x]').value = String(draft.printTransform.x * 100);
        query('[data-nfc-print-y]').value = String(draft.printTransform.y * 100);
      }
      if (config.family === 'record' || config.family === 'discs') {
        cropContext.save();
        cropContext.beginPath();
        cropContext.rect(0, 0, edge, edge);
        cropContext.arc(edge / 2, edge / 2, edge / 2, 0, Math.PI * 2, true);
        cropContext.fillStyle = '#302c2866';
        cropContext.fill('evenodd');
        cropContext.beginPath();
        cropContext.arc(edge / 2, edge / 2, edge * 0.42, 0, Math.PI * 2);
        cropContext.strokeStyle = '#ffffff';
        cropContext.lineWidth = 2;
        cropContext.setLineDash([8, 7]);
        cropContext.stroke();
        cropContext.restore();
      }
      const zoom = Math.min(slot.width, slot.height) / size;
      query('[data-nfc-crop-zoom]').value = String(zoom);
      query('[data-nfc-zoom-label]').value = `${zoom.toFixed(2)}×`;
      query('[data-nfc-crop-x]').value = String(slot.width === size ? 50 : x / (slot.width - size) * 100);
      query('[data-nfc-crop-y]').value = String(slot.height === size ? 50 : y / (slot.height - size) * 100);
      const quality = query('[data-nfc-crop-quality]');
      quality.dataset.low = String(size < 512);
      quality.textContent = size < 512 ? `Low resolution: ${Math.floor(size)} × ${Math.floor(size)} px. Recommended minimum: 512 px; print may look soft.` : `${Math.floor(size)} × ${Math.floor(size)} px crop · saved as a 1024 × 1024 JPG`;
    }

    function openCrop(index) {
      if (locked() || !slots[index]) return;
      activeIndex = index;
      draft = { index, crop: { ...(slots[index].crop || fitCrop(slots[index])) }, printTransform: printTransform(slots[index].printTransform) };
      drag = null;
      query('[data-nfc-crop-title]').textContent = `Crop photo ${numberLabel(index)}`;
      query('[data-nfc-crop-error]').textContent = '';
      const next = slots.some((slot, slotIndex) => slot && !slot.confirmed && slotIndex !== index);
      query('[data-nfc-crop-confirm]').textContent = next ? 'Confirm & next' : 'Confirm crop';
      drawCrop();
      updatePreview();
      if (!dialog.open) {
        returnFocus = document.activeElement;
        dialog.showModal();
      }
      cropCanvas.focus();
    }

    function closeCrop() {
      if (editingBusy) return;
      draft = null;
      drag = null;
      dialog.close();
      if (returnFocus?.isConnected && !returnFocus.disabled) returnFocus.focus();
    }

    async function importFiles(files, targetIndex = null) {
      if (locked() || !files.length) return;
      const available = slots.reduce((indexes, slot, index) => {
        if (!slot) indexes.push(index);
        return indexes;
      }, []);
      const capacity = targetIndex === null ? available.length : 1;
      if (files.length > capacity) {
        announce(`Choose ${capacity === 1 ? '1 photo' : `up to ${capacity} photos`}. Your selection has ${files.length}; no files were added. Remove or replace a photo to make room.`, 'error', true);
        return;
      }
      const invalid = files.find((file) => !supportedTypes.has(file.type) || !file.size || file.size > maximumFileSize);
      if (invalid) {
        announce(`“${invalid.name}” must be a non-empty JPG, PNG or WEBP of 10 MB or less. No files were added.`, 'error', true);
        return;
      }
      editingBusy = true;
      synchronize();
      announce('Opening your photos…', 'info');
      const staged = [];
      try {
        for (const file of files) {
          const sourceUrl = createUrl(file);
          const slot = { file, sourceUrl, image: null, blob: null, cropUrl: null, crop: null, confirmed: false };
          staged.push(slot);
          slot.image = await loadImage(sourceUrl);
          slot.width = slot.image.naturalWidth;
          slot.height = slot.image.naturalHeight;
          if (config.family === 'cassette' && slot.width !== slot.height) throw new Error(`“${file.name}” is ${slot.width} × ${slot.height} px. Cassette uploads must be square (width = height). Crop it to a square first, then upload again. No files were added.`);
          if (slot.width > 16384 || slot.height > 16384 || slot.width * slot.height > 40000000) throw new Error(`“${file.name}” is too large to edit safely. Resize it to 40 megapixels or less, with neither side over 16,384 px. No files were added.`);
        }
        if (disposed) throw new Error('The editor was closed.');
        staged.forEach((slot, index) => {
          const slotIndex = targetIndex === null ? available[index] : targetIndex;
          releaseSlot(slots[slotIndex]);
          slots[slotIndex] = slot;
        });
        changeDesign();
        editingBusy = false;
        synchronize();
        updatePreview();
        announce(`${files.length} ${files.length === 1 ? 'photo added' : 'photos added'}. Confirm each square crop.`, 'info');
        openCrop(targetIndex === null ? available[0] : targetIndex);
      } catch (error) {
        staged.forEach(releaseSlot);
        announce(error.message, 'error', true);
      } finally {
        editingBusy = false;
        synchronize();
      }
    }

    async function confirmCrop() {
      if (locked() || !draft) return;
      const index = draft.index;
      const slot = slots[index];
      if (config.family === 'cassette' && slot.width !== slot.height) {
        query('[data-nfc-crop-error]').textContent = 'This older original is not square. Cancel and replace it with a square file before confirming the print composition.';
        return;
      }
      const crop = boundedCrop(draft.crop, slot);
      let cropUrl;
      editingBusy = true;
      synchronize();
      query('[data-nfc-crop-error]').textContent = '';
      try {
        const canvas = document.createElement('canvas');
        canvas.width = outputSize;
        canvas.height = outputSize;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('This browser cannot create your crop. Try another browser.');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, outputSize, outputSize);
        context.imageSmoothingQuality = 'high';
        context.drawImage(slot.image, crop.x, crop.y, crop.size, crop.size, 0, 0, outputSize, outputSize);
        const blob = await jpegBlob(canvas);
        cropUrl = createUrl(blob);
        const croppedImage = await loadImage(cropUrl);
        if (disposed) throw new Error('The editor was closed.');
        revokeUrl(slot.cropUrl);
        const changed = !slot.confirmed || !slot.crop || ['x', 'y', 'size'].some((key) => Math.abs(slot.crop[key] - crop[key]) > 0.000001) || config.family === 'cassette' && ['x', 'y', 'zoom'].some(key => printTransform(slot.printTransform)[key] !== draft.printTransform[key]);
        Object.assign(slot, { blob, cropUrl, crop, croppedImage, confirmed: true });
        if (config.family === 'cassette') {
          slot.printTransform = printTransform(draft.printTransform);
          slot.printTransforms = { ...(slot.printTransforms || {}), [layoutId]: slot.printTransform };
        }
        if (changed) changeDesign();
        editingBusy = false;
        synchronize();
        updatePreview();
        closeCrop();
        const next = slots.findIndex((candidate) => candidate && !candidate.confirmed);
        if (next >= 0) openCrop(next);
        else announce('All uploaded crops are confirmed. Your squares are saved exactly as shown.', 'info');
      } catch (error) {
        revokeUrl(cropUrl);
        query('[data-nfc-crop-error]').textContent = error.message;
      } finally {
        editingBusy = false;
        synchronize();
      }
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (locked() || successfulDesignId === designId) return;
      if (dialog.open) {
        query('[data-nfc-crop-error]').textContent = 'Confirm or cancel this crop before adding your set.';
        return;
      }
      if (!selectedVariant()?.available) {
        announce('This custom set is currently unavailable.', 'error', true);
        return;
      }
      if (confirmedCount() !== imageCount) {
        announce(`Add exactly ${imageCount} ${imageCount === 1 ? 'single-person portrait' : 'photos'} and confirm every square crop before continuing.`, 'error', true);
        return;
      }
      const tracks = wantsMusic() ? trackValues(true) : null;
      if (tracks?.firstInvalid) {
        announce('Each of the 9 tracks needs a song title and artist. Keep titles and artists to 120 characters and versions to 50.');
        tracks.firstInvalid.focus();
        return;
      }
      if (!acknowledgement.checked) {
        announce('Check the acknowledgement to approve your current crops and track details.', 'error', true);
        acknowledgement.focus();
        return;
      }
      preparing = true;
      synchronize();
      const snapshotId = designId;
      const snapshotRevision = revision;
      const variant = selectedVariant();
      const musicRequested = wantsMusic();
      try {
        const composite = document.createElement('canvas');
        composite.width = outputSize;
        composite.height = outputSize;
        const context = composite.getContext('2d');
        if (!context) throw new Error('This browser cannot export your preview. Try another browser.');
        paintPreview(context, false);
        const previewBlob = await jpegBlob(composite);
        if (disposed || designId !== snapshotId || revision !== snapshotRevision) throw new Error('Your design changed. Please check it and try again.');
        const formData = new FormData();
        formData.append('id', String(variant.id));
        formData.append('quantity', '1');
        formData.append('properties[Design ID]', snapshotId);
        formData.append('properties[Player type]', { record: 'Record player', cassette: 'Cassette player', rotating: 'Rotating music box', discs: 'Disc accessory set' }[config.family]);
        formData.append('properties[Customization]', config.family === 'rotating' ? '1 character portrait' : musicRequested ? '9 photos + 9 tracks' : '9 photos');
        if (config.family === 'cassette') formData.append('properties[Print layout]', selectedLayout().label);
        slots.forEach((slot, index) => {
          if (!slot.confirmed || !slot.blob || !slot.crop) throw new Error(`Please confirm ${imageCount === 1 ? 'your portrait crop' : `all ${imageCount} crops`} again.`);
          formData.append(`properties[Image ${numberLabel(index)}]`, slot.blob, `${snapshotId}-${numberLabel(index)}.jpg`);
        });
        formData.append('properties[Preview]', previewBlob, `${snapshotId}-preview.jpg`);
        const cropMap = { v: 2, output: outputSize, family: config.family, images: slots.map((slot, index) => ({ n: index + 1, w: slot.width, h: slot.height, x: Number(slot.crop.x.toFixed(4)), y: Number(slot.crop.y.toFixed(4)), size: Number(slot.crop.size.toFixed(4)) })) };
        if (config.family === 'record') cropMap.panel = { layout: '3x3-row-major', order: [1,2,3,4,5,6,7,8,9], mountedDisc: 1 };
        else if (config.family === 'discs') cropMap.preview = { kind: 'disc-artwork', stickerNumbers: [10,11,12,13,14,15,16,17,18], artworkShape: 'circle', finalPrintTemplate: false, playerIncluded: false };
        else if (config.family === 'cassette') {
          cropMap.printLayout = printLayout();
          cropMap.images.forEach((image, index) => { image.printTransform = printTransform(slots[index].printTransform); });
          cropMap.preview = cassettePreview.metadata(layoutId);
        }
        else cropMap.preview = { kind: 'flat-artwork', layout: imageCount === 1 ? 'single-square' : '3x3-row-major', finalPrintTemplate: false };
        formData.append('properties[Crop map]', JSON.stringify(cropMap));
        if (musicRequested) tracks.values.forEach((track, index) => formData.append(`properties[Track ${numberLabel(index)}]`, track));
        preparing = false;
        pending = true;
        submittedDesignId = snapshotId;
        synchronize();
        await persistNow();
        announce('Adding your custom set. Please keep this page open.', 'info');
        document.dispatchEvent(new CustomEvent('nfc:custom-submit', { detail: { root, formData, variantId: String(variant.id), designId: snapshotId, musicRequested, ...(config.family === 'cassette' ? { layoutId } : {}) } }));
      } catch (error) {
        preparing = false;
        pending = false;
        announce(error.message, 'error', true);
        synchronize();
      }
    });

    root.addEventListener('nfc:custom-lock', (event) => {
      externalLocked = !!event.detail?.locked || !!event.detail?.pending;
      if (event.detail?.uncertain) {
        uncertain = true;
        pending = false;
        query('[data-nfc-cart-link]').hidden = false;
        announce('We could not confirm this addition. Check your cart before continuing; do not submit it again.', 'uncertain');
      }
      synchronize();
    });

    root.addEventListener('nfc:custom-result', async (event) => {
      const result = event.detail || {};
      if (!['success', 'rejected', 'uncertain', 'blocked', 'invalid'].includes(result.kind)) return;
      preparing = false;
      pending = false;
      if (result.kind === 'success') {
        successfulDesignId = submittedDesignId || designId;
        uncertain = false;
        externalLocked = false;
        query('[data-nfc-cart-link]').hidden = false;
        announce(result.message || 'Added to cart. This design cannot be added twice.', 'success');
      } else if (result.kind === 'uncertain') {
        uncertain = true;
        externalLocked = true;
        query('[data-nfc-cart-link]').hidden = false;
        announce(`${result.message || 'Addition unconfirmed.'} Check your cart. Do not resubmit; no automatic retry.`, 'uncertain', true);
      } else if (uncertain) {
        externalLocked = true;
        query('[data-nfc-cart-link]').hidden = false;
        announce(`${result.message || 'Addition still unconfirmed.'} Check your cart. Do not resubmit; no automatic retry.`, 'uncertain', true);
      } else {
        if (result.kind !== 'blocked') externalLocked = false;
        announce(result.message || 'Your set was not added. Check your details and try again.', 'error', true);
      }
      synchronize();
      await persistNow();
    });

    query('[data-nfc-cart-link]').addEventListener('click', async (event) => {
      if (!slots.some(Boolean) || root.dataset.designStorage === 'saved' && storedSignature === persistenceSignature()) return;
      event.preventDefault();
      const destination = event.currentTarget.href;
      await persistNow();
      if (!storageProblem) window.location.assign(destination);
    });
    query('[data-nfc-clear-local]').addEventListener('click', async () => {
      if (locked()) return;
      if (!window.confirm('Clear saved designs for this player and selected option in this browser? Items already in your cart will stay.')) return;
      clearTimeout(persistTimer);
      editingBusy = true;
      synchronize();
      await saveQueue;
      try { await window.NFCDesignStore?.forget(persistenceOwner()); } catch { storageProblem = true; }
      slots.forEach(releaseSlot);
      slots.fill(null);
      trackRows.forEach(row => row.querySelectorAll('input').forEach(input => { input.value = ''; }));
      successfulDesignId = null;
      submittedDesignId = null;
      changeDesign();
      storedSignature = '';
      queuedSignature = '';
      const clearedUrl = new URL(window.location.href);
      clearedUrl.searchParams.delete('design');
      clearedUrl.searchParams.set('variant', String(selectedVariant().id));
      window.history.replaceState({}, '', clearedUrl);
      editingBusy = false;
      query('[data-nfc-cart-link]').hidden = true;
      updatePreview();
      synchronize();
      if (storageStatus) storageStatus.textContent = storageProblem ? 'Private storage is unavailable.' : 'Browser drafts cleared. Cart items unchanged.';
    });

    query('[data-nfc-upload-button]').addEventListener('click', () => { if (!locked()) upload.click(); });
    upload.addEventListener('change', () => {
      const files = Array.from(upload.files || []);
      upload.value = '';
      void importFiles(files);
    });
    replace.addEventListener('change', () => {
      const files = Array.from(replace.files || []);
      replace.value = '';
      const index = replacementIndex;
      replacementIndex = null;
      if (index !== null) void importFiles(files, index);
    });
    tiles.forEach((tile, index) => {
      tile.querySelector('[data-nfc-edit]').addEventListener('click', () => {
        if (locked()) return;
        if (slots[index]) openCrop(index);
        else {
          replacementIndex = index;
          replace.click();
        }
      });
      tile.querySelector('[data-nfc-replace-button]').addEventListener('click', () => {
        if (locked()) return;
        replacementIndex = index;
        replace.click();
      });
      tile.querySelector('[data-nfc-remove]').addEventListener('click', () => {
        if (locked()) return;
        releaseSlot(slots[index]);
        slots[index] = null;
        changeDesign();
        synchronize();
        updatePreview();
        announce(`Photo ${numberLabel(index)} removed. Add and confirm a replacement.`, 'info');
      });
    });
    cassetteThumbs.forEach((button, index) => button.addEventListener('click', () => {
      if (!locked()) tiles[index].querySelector('[data-nfc-edit]').click();
    }));
    const dropZone = query('[data-nfc-drop-zone]');
    dropZone.addEventListener('dragover', (event) => {
      event.preventDefault();
      if (!locked()) dropZone.dataset.dragging = 'true';
    });
    dropZone.addEventListener('dragleave', () => { delete dropZone.dataset.dragging; });
    dropZone.addEventListener('drop', (event) => {
      event.preventDefault();
      delete dropZone.dataset.dragging;
      void importFiles(Array.from(event.dataTransfer?.files || []));
    });
    music?.addEventListener('change', () => {
      if (locked()) return;
      changeDesign();
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(selectedVariant().id));
      url.searchParams.delete('design');
      window.history.replaceState({}, '', url);
      synchronize();
    });
    layoutButtons.forEach(button => button.addEventListener('click', () => {
      const nextId = button.dataset.nfcLayout;
      if (locked() || dialog.open || nextId === layoutId || !config.layouts.some(layout => layout.id === nextId)) return;
      void persistNow();
      slots.forEach(slot => {
        if (!slot) return;
        slot.printTransforms = { ...(slot.printTransforms || {}), [layoutId]: printTransform(slot.printTransform) };
        slot.printTransform = printTransform(slot.printTransforms[nextId]);
        slot.confirmed = false;
      });
      layoutId = nextId;
      changeDesign();
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(selectedVariant().id));
      url.searchParams.delete('design');
      window.history.replaceState({}, '', url);
      synchronize();
      updatePreview();
      announce(`${selectedLayout().label} selected. Photos, square crops and tracks are kept. Confirm all 9 print compositions again.`, 'info');
    }));
    trackRows.forEach((row) => row.querySelectorAll('input').forEach((input) => input.addEventListener('input', () => {
      if (locked() || !wantsMusic()) return;
      input.removeAttribute('aria-invalid');
      changeDesign();
      synchronize();
    })));
    acknowledgement.addEventListener('change', synchronize);
    query('[data-nfc-crop-close]').addEventListener('click', closeCrop);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      if (!editingBusy) closeCrop();
    });
    query('[data-nfc-crop-reset]').addEventListener('click', () => {
      if (locked() || !draft) return;
      draft.crop = fitCrop(slots[draft.index]);
      draft.printTransform = printTransform();
      drawCrop();
    });
    ['x', 'y', 'zoom'].forEach(axis => query(`[data-nfc-print-${axis}]`)?.addEventListener('input', event => {
      if (locked() || !draft) return;
      draft.printTransform = printTransform({ ...draft.printTransform, [axis]: Number(event.target.value) / (axis === 'zoom' ? 1 : 100) });
      drawCrop();
    }));
    query('[data-nfc-crop-confirm]').addEventListener('click', () => { void confirmCrop(); });
    query('[data-nfc-crop-zoom]').addEventListener('input', (event) => {
      if (locked() || !draft) return;
      const slot = slots[draft.index];
      const size = Math.min(slot.width, slot.height) / clamp(Number(event.target.value), 1, 8);
      draft.crop = { x: draft.crop.x + (draft.crop.size - size) / 2, y: draft.crop.y + (draft.crop.size - size) / 2, size };
      drawCrop();
    });
    ['x', 'y'].forEach((axis) => query(`[data-nfc-crop-${axis}]`).addEventListener('input', (event) => {
      if (locked() || !draft) return;
      const slot = slots[draft.index];
      draft.crop[axis] = (slot[axis === 'x' ? 'width' : 'height'] - draft.crop.size) * clamp(Number(event.target.value), 0, 100) / 100;
      drawCrop();
    }));
    cropCanvas.addEventListener('pointerdown', (event) => {
      if (locked() || !draft || (event.pointerType === 'mouse' && event.button !== 0)) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, crop: { ...draft.crop } };
      cropCanvas.setPointerCapture(event.pointerId);
      cropCanvas.focus();
      event.preventDefault();
    });
    cropCanvas.addEventListener('pointermove', (event) => {
      if (!drag || drag.id !== event.pointerId || locked() || !draft) return;
      const bounds = cropCanvas.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      draft.crop.x = drag.crop.x - (event.clientX - drag.x) * drag.crop.size / bounds.width;
      draft.crop.y = drag.crop.y - (event.clientY - drag.y) * drag.crop.size / bounds.height;
      drawCrop();
    });
    const finishDrag = (event) => {
      if (drag?.id !== event.pointerId) return;
      if (cropCanvas.hasPointerCapture(event.pointerId)) cropCanvas.releasePointerCapture(event.pointerId);
      drag = null;
    };
    cropCanvas.addEventListener('pointerup', finishDrag);
    cropCanvas.addEventListener('pointercancel', finishDrag);
    cropCanvas.addEventListener('lostpointercapture', () => { drag = null; });
    cropCanvas.addEventListener('keydown', (event) => {
      if (locked() || !draft || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      const step = draft.crop.size / (event.shiftKey ? 10 : 100);
      if (event.key === 'ArrowLeft') draft.crop.x -= step;
      if (event.key === 'ArrowRight') draft.crop.x += step;
      if (event.key === 'ArrowUp') draft.crop.y -= step;
      if (event.key === 'ArrowDown') draft.crop.y += step;
      drawCrop();
    });

    function dispose() {
      clearTimeout(persistTimer);
      disposed = true;
      ready = false;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.clear();
      slots.fill(null);
      draft = null;
      drag = null;
      baseImage = null;
      cassettePreview = null;
      if (dialog.open) dialog.close();
      synchronize();
      announce('Your private editor session has ended. Reload to start a new design.', 'error');
    }

    window.addEventListener('beforeunload', (event) => {
      if (disposed || !slots.some(Boolean) || storedSignature === persistenceSignature()) return;
      event.preventDefault();
      event.returnValue = '';
    });
    window.addEventListener('pagehide', (event) => { if (!event.persisted) dispose(); });
    document.addEventListener('shopify:section:unload', (event) => { if (event.target.contains(root)) dispose(); });

    async function start() {
      try {
        config = JSON.parse(query('[data-nfc-custom-config]').textContent);
        const validVariant = (variant) => variant && /^\d+$/.test(String(variant.id)) && Number.isFinite(variant.price) && variant.price > 0 && typeof variant.formattedPrice === 'string' && typeof variant.available === 'boolean';
        const portrait = config.family === 'rotating';
        const validMusic = portrait || config.family === 'discs' ? config.musicVariant === null && !music && trackRows.length === 0 : validVariant(config.musicVariant) && String(config.photoVariant?.id) !== String(config.musicVariant.id) && !!music && trackRows.length === 9;
        if (!['record', 'cassette', 'rotating', 'discs'].includes(config.family) || config.imageCount !== (portrait ? 1 : 9) || tiles.length !== config.imageCount || !validVariant(config.photoVariant) || !validMusic || config.family === 'record' && (typeof config.baseImage !== 'string' || !config.baseImage)) throw new Error('This editor is not configured for ordering yet. Please try again later.');
        imageCount = config.imageCount;
        slots.push(...Array(imageCount).fill(null));
        root.dataset.family = config.family;
        const requestedVariant = new URL(window.location.href).searchParams.get('variant');
        const initialVariantId = String(requestedVariant ?? config.initialVariantId ?? config.photoVariant.id);
        if (config.family === 'cassette') {
          const expectedLayouts = { 'side-panel': 'Side Panel', 'two-round-windows': 'Two Round Windows', 'center-window': 'Center Window', 'full-artwork': 'Full Artwork' };
          if (!Array.isArray(config.layouts) || config.layouts.length !== 4 || new Set(config.layouts.map(layout => layout.id)).size !== 4 || config.layouts.some(layout => expectedLayouts[layout.id] !== layout.label || !validVariant(layout.photoVariant) || !validVariant(layout.musicVariant) || layout.photoVariant.price !== 9999 || layout.musicVariant.price !== 10999) || new Set(config.layouts.flatMap(layout => [String(layout.photoVariant.id), String(layout.musicVariant.id)])).size !== 8) throw new Error('The 4 cassette layouts and their exact prices are not configured. Ordering is disabled.');
          layoutId = config.layouts.find(layout => [layout.photoVariant.id, layout.musicVariant.id].some(identifier => String(identifier) === initialVariantId))?.id || null;
        }
        if (config.family === 'cassette' && !selectedLayout() || ![variantPair().photoVariant.id, variantPair().musicVariant?.id].filter(Boolean).some(identifier => String(identifier) === initialVariantId)) throw new Error('This variant does not match this custom editor. Reopen the matching custom option from the product page.');
        if (music) music.checked = initialVariantId === String(variantPair().musicVariant.id);
        if (!preview.getContext || !preview.toBlob || !cropCanvas.getContext || !dialog.showModal || !window.File || !window.FormData || !window.crypto?.getRandomValues || !URL.createObjectURL) throw new Error('Your browser cannot safely create custom images. Please use a current browser.');
        previewContext = preview.getContext('2d');
        cropContext = cropCanvas.getContext('2d');
        if (!previewContext || !cropContext) throw new Error('Canvas is unavailable in this browser. Please use another browser.');
        designId = makeDesignId();
        if (config.family === 'record') baseImage = await loadImage(config.baseImage, true);
        if (config.family === 'cassette') {
          if (!window.NFCCassettePreview?.load) throw new Error('The cassette placement preview is unavailable. Please reload before customizing.');
          cassettePreview = await window.NFCCassettePreview.load(config, loadImage);
        }
        if (disposed) return;
        await restoreDesign();
        paintPreview(previewContext, true);
        await jpegBlob(preview);
        if (disposed) return;
        ready = true;
        synchronize();
        updatePreview();
      } catch (error) {
        ready = false;
        if (config?.family === 'cassette') previewContext?.clearRect(0, 0, outputSize, outputSize);
        previewStatus.textContent = 'Preview unavailable. Custom ordering is disabled.';
        announce(error.message || 'The preview could not load. Reload before starting your design.', 'error');
        synchronize();
      }
    }
    void start();
  }

  document.querySelectorAll('[data-nfc-custom]').forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-nfc-custom]').forEach(initialize));
})();
